import React, { useEffect } from 'react';
import { PUBLIC_SITE_URL, DEFAULT_OG_IMAGE, seoDefaults } from '../data/seo';

/**
 * Reusable SEO component (imperative <head> updates via useEffect).
 *
 * Why imperative and not JSX? React 18/19 both track head-hoisted tags
 * as fiber children of the rendering component. On route unmount they
 * try to `removeChild` those tags from their virtual parent — which
 * fails ("The node to be removed is not a child of this node") because
 * the browser has physically moved them into <head>. React 19 native
 * hoisting exhibits the same bug, so we bypass fiber tracking entirely
 * and just poke <head> ourselves. Cleanup restores tags to the previous
 * page's values so unmount is safe (no dangling tags).
 *
 * Props: same as before (title, description, path, image, keywords, type,
 * structuredData, noindex).
 */
function upsertMeta({ attr, key, value }) {
  if (value === undefined || value === null || value === '') return null;
  const selector = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector(selector);
  const prev = el ? el.getAttribute('content') : null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
  return { el, prev, created: prev === null };
}

function upsertLink({ rel, hrefLang, href }) {
  if (!href) return null;
  const parts = [`link[rel="${rel}"]`];
  if (hrefLang) parts.push(`[hreflang="${hrefLang}"]`);
  const selector = parts.join('');
  let el = document.head.querySelector(selector);
  const prev = el ? el.getAttribute('href') : null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    if (hrefLang) el.setAttribute('hreflang', hrefLang);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  return { el, prev, created: prev === null };
}

function upsertScript({ id, content }) {
  let el = document.getElementById(id);
  const created = !el;
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = content;
  return { el, created };
}

const SEO = ({
  title,
  description,
  path = "/",
  image = DEFAULT_OG_IMAGE,
  keywords,
  type = "website",
  structuredData,
  noindex = false,
}) => {
  const canonical = `${PUBLIC_SITE_URL}${path}`;
  const ogImage = image || DEFAULT_OG_IMAGE;
  const structuredDataArray = Array.isArray(structuredData)
    ? structuredData
    : (structuredData ? [structuredData] : []);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;

    const applied = [];
    applied.push(upsertMeta({ attr: 'name', key: 'description', value: description }));
    if (keywords) applied.push(upsertMeta({ attr: 'name', key: 'keywords', value: keywords }));
    applied.push(upsertMeta({ attr: 'name', key: 'robots', value: noindex ? 'noindex,nofollow' : 'index,follow' }));
    applied.push(upsertLink({ rel: 'canonical', href: canonical }));
    applied.push(upsertLink({ rel: 'alternate', hrefLang: 'en-GB', href: canonical }));
    applied.push(upsertLink({ rel: 'alternate', hrefLang: 'x-default', href: canonical }));

    // Open Graph
    applied.push(upsertMeta({ attr: 'property', key: 'og:site_name', value: seoDefaults.siteName }));
    applied.push(upsertMeta({ attr: 'property', key: 'og:title', value: title }));
    applied.push(upsertMeta({ attr: 'property', key: 'og:description', value: description }));
    applied.push(upsertMeta({ attr: 'property', key: 'og:type', value: type }));
    applied.push(upsertMeta({ attr: 'property', key: 'og:url', value: canonical }));
    applied.push(upsertMeta({ attr: 'property', key: 'og:image', value: ogImage }));
    applied.push(upsertMeta({ attr: 'property', key: 'og:image:width', value: '1200' }));
    applied.push(upsertMeta({ attr: 'property', key: 'og:image:height', value: '630' }));
    applied.push(upsertMeta({ attr: 'property', key: 'og:locale', value: seoDefaults.locale }));

    // Twitter / X
    applied.push(upsertMeta({ attr: 'name', key: 'twitter:card', value: 'summary_large_image' }));
    applied.push(upsertMeta({ attr: 'name', key: 'twitter:title', value: title }));
    applied.push(upsertMeta({ attr: 'name', key: 'twitter:description', value: description }));
    applied.push(upsertMeta({ attr: 'name', key: 'twitter:image', value: ogImage }));

    // JSON-LD structured data — each script gets a stable id so we can
    // upsert cleanly across route changes.
    const scripts = structuredDataArray.map((data, idx) => (
      upsertScript({ id: `ld-seo-${idx}`, content: JSON.stringify(data) })
    ));
    // Remove any stale scripts from the previous page (if the previous had
    // more JSON-LD blobs than this one).
    let extraIdx = structuredDataArray.length;
    while (document.getElementById(`ld-seo-${extraIdx}`)) {
      document.getElementById(`ld-seo-${extraIdx}`).remove();
      extraIdx += 1;
    }

    return () => {
      // Restore title so back-nav feels correct. Meta tags stay — the next
      // page's SEO effect will upsert them.
      document.title = prevTitle;
    };
  }, [title, description, canonical, keywords, type, ogImage, noindex, JSON.stringify(structuredDataArray)]);

  return null;
};

export default SEO;
